import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  Breadcrumbs,
  Link,
  Fade,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { getConfigCrudDefinition } from './configCrudDefinitions';

const buildInitialForm = (fields) => fields.reduce((acc, field) => ({
  ...acc,
  [field.name]: field.type === 'checkbox' ? false : '',
}), {});

const getPlaceholder = (field) => field.placeholder || `Enter ${field.label}`;

const getWorkspaceTitle = (definition, isEditMode) => {
  if (!definition) return '';
  const baseTitle = definition.title.toLowerCase().includes('model')
    ? 'Attribute'
    : definition.title.replace(/^HCP\s|^Drug\s/i, '').replace(/s$/, '');
  return `${isEditMode ? 'Edit' : 'Add'} ${baseTitle}`;
};

const softInputSx = {
  '& .MuiOutlinedInput-root': {
    // backgroundColor: '#f8fafc',
    borderRadius: '4px',
    transition: 'all 0.15s ease',
    '&:not(.MuiInputBase-multiline)': {
      height: '32px',
    },
    '&.MuiInputBase-multiline': {
      padding: '8px 10px',
      alignItems: 'flex-start',
    },
    '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#2196f3', borderWidth: '1px' },
      boxShadow: '0 2px 6px rgba(33, 150, 243, 0.06)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 10px',
    color: '#0f172a',
    boxSizing: 'border-box',
    '&:not(.MuiInputBase-inputMultiline)': {
      height: '32px',
    },
    '&.MuiInputBase-inputMultiline': {
      padding: 0,
      lineHeight: 1.5,
    },
    '&::placeholder': { color: '#94a3b8', opacity: 1 },
  },
  '& .MuiSelect-select': {
    padding: '0 10px !important',
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    color: '#0f172a',
    boxSizing: 'border-box',
  },
};

const fieldLabelSx = {
  
  fontWeight: 600,
  color: '#475569',
  letterSpacing: '0.01em',
  mb: 0.4,
  display: 'block',
};

const helperTextSx = {
  
  color: '#9ca3af',
  mt: 0.5,
  lineHeight: 1.5,
};

const pageHeaderSx = {
  px: 2.25,
  py: 1.5,
  borderBottom: '1px solid #E5E7EB',
  borderLeft: '4px solid #1E88E5',
  display: 'flex',
  backgroundColor: '#fbfdfe',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
};

const selectPlaceholderSx = { color: '#94a3b8',  };

const secondaryButtonSx = {
  minWidth: 88,
  px: 2.5,
  py: 0.9,
  textTransform: 'none',
  fontWeight: 500,
  
  borderRadius: '8px',
  color: '#475569',
  borderColor: '#e2e8f0',
  backgroundColor: '#fff',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', boxShadow: 'none' },
};

const primaryButtonSx = {
  minWidth: 100,
  px: 3,
  py: 0.9,
  textTransform: 'none',
  transition: 'all 0.18s ease',
};

const ConfigCrudWorkspacePage = ({ entityType: entityTypeProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entityType: entityTypeParam, section, mode, itemKey } = useParams();
  const entityType = entityTypeProp || entityTypeParam;
  const definition = getConfigCrudDefinition(entityType, section);
  const [localError, setLocalError] = useState('');
  const [form, setForm] = useState({});

  const initialForm = useMemo(
    () => (definition ? buildInitialForm(definition.fields) : {}),
    [definition]
  );

  const getRowKey = useMemo(
    () => definition?.getRowKey || ((row) => row.name || row.displayName || row.attribute || row.id || JSON.stringify(row)),
    [definition]
  );

  const backPath = useMemo(() => `/configuration/${entityType}/${section}`, [entityType, section]);
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (!definition) return;
    if (!isEditMode) { setForm(initialForm); return; }

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try { rows = cached ? JSON.parse(cached) : []; } catch { rows = []; }

    const stateRow = location.state?.row;
    const matchedRow = stateRow || rows.find((row) => String(getRowKey(row)) === decodeURIComponent(itemKey || ''));

    if (!matchedRow) {
      setLocalError(`Unable to find the selected ${definition.title.toLowerCase()} item.`);
      setForm(initialForm);
      return;
    }

    setForm(definition.fields.reduce((acc, field) => ({
      ...acc,
      [field.name]: matchedRow?.[field.name] ?? (field.type === 'checkbox' ? false : ''),
    }), {}));
  }, [definition, getRowKey, initialForm, isEditMode, itemKey, location.state]);

  const onChange = (field) => (event) => {
    const value = field.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field.name]: value }));
  };

  const onSave = useCallback(() => {
    if (!definition) return;

    const requiredField = definition.fields.find((field) => field.required && !form[field.name]);
    if (requiredField) { setLocalError(`${requiredField.label} is required.`); return; }

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try { rows = cached ? JSON.parse(cached) : []; } catch { rows = []; }

    const nextRow = { ...form };
    let nextRows = rows;

    if (isEditMode) {
      const currentKey = decodeURIComponent(itemKey || '');
      nextRows = rows.map((row) => String(getRowKey(row)) === currentKey ? { ...row, ...nextRow } : row);
    } else {
      nextRows = [...rows, nextRow];
    }

    localStorage.setItem(definition.storageKey, JSON.stringify(nextRows));
    localStorage.setItem(`${definition.storageKey}__version`, definition.storageVersion);
    navigate(backPath);
  }, [backPath, definition, form, getRowKey, isEditMode, itemKey, navigate]);

  if (!definition) {
    return <Alert severity="error">Configuration workspace is not available for this tab.</Alert>;
  }

  const regularFields = definition.fields.filter((f) => f.name !== 'description' && f.type !== 'checkbox');
  const bottomFields = definition.fields.filter((f) => f.name === 'description' || f.type === 'checkbox');

  return (
    <Box sx={{ px: 1, pb: 4, mt: 1 }}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs items={[
          // { label: 'Configuration', path: `/configuration/${entityType}/model` },
          { label: definition.title, path: backPath },
          { label: getWorkspaceTitle(definition, isEditMode) }
        ]} />
      </Box>

      <Fade in timeout={350}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
            {/* Header */}
            <Box sx={pageHeaderSx}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                  {getWorkspaceTitle(definition, isEditMode)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {isEditMode ? 'Update the details for this record.' : `Configure and save a new ${definition.title.replace(/s$/, '').toLowerCase()}.`}
                </Typography>
              </Box>
            </Box>

          {/* Fields */}
          <Box sx={{ px: 3.5, py: 3 }}>
            <Grid container spacing={2.5}>
              {regularFields.map((field) => (
                <Grid item xs={12} md={4} key={field.name}>
                  <Typography component="label" sx={fieldLabelSx}>
                    {field.label}
                    {field.required && (
                      <Box component="span" sx={{ color: '#ef4444', ml: 0.3 }}>*</Box>
                    )}
                  </Typography>
                  <TextField
                    sx={softInputSx}
                    fullWidth
                    select={field.type === 'select'}
                    placeholder={getPlaceholder(field)}
                    value={form[field.name] ?? ''}
                    onChange={onChange(field)}
                    multiline={field.type === 'textarea'}
                    minRows={field.type === 'textarea' ? 3 : undefined}
                    type={field.type === 'number' ? 'number' : 'text'}
                    size="small"
                    InputLabelProps={{ shrink: false }}
                    SelectProps={field.type === 'select' ? {
                      displayEmpty: true,
                      renderValue: (value) => (value
                        ? value
                        : <Box component="span" sx={selectPlaceholderSx}>{field.placeholder || `Select ${field.label}`}</Box>),
                    } : undefined}
                  >
                    {field.type === 'select'
                      ? [
                        <MenuItem key="" value="" disabled>
                          <em style={{ color: '#94a3b8', fontStyle: 'normal' }}>
                            {field.placeholder || `Select ${field.label}`}
                          </em>
                        </MenuItem>,
                        ...(field.options || []).map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        )),
                      ]
                      : null}
                  </TextField>
                  {field.helperText && (
                    <Typography sx={helperTextSx}>{field.helperText}</Typography>
                  )}
                </Grid>
              ))}
            </Grid>

            {bottomFields.length > 0 && (
              <>
                <Divider sx={{ my: 1.3, borderColor: '#f1f5f9' }} />
                <Grid container spacing={2} alignItems="stretch">
                  {bottomFields.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.name} sx={{ display: 'flex', flexDirection: 'column' }}>
                      {field.type === 'checkbox' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.75,
                            borderRadius: '8px',
                            border: '1px solid #f1f5f9',
                            backgroundColor: '#fafafa',
                            transition: 'border-color 0.15s',
                            // flex: 1,
                            mt: 2.7,
                            height: '5rem',
                            '&:hover': { borderColor: '#e2e8f0' },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: '#374151' }}>
                              {field.label}
                            </Typography>
                            {field.helperText && (
                              <Typography sx={{ ...helperTextSx, mt: 0.2 }}>{field.helperText}</Typography>
                            )}
                          </Box>
                          <FormControlLabel
                            sx={{ m: 0, ml: 1.5, flexShrink: 0 }}
                            control={
                              <Switch
                                checked={Boolean(form[field.name])}
                                onChange={onChange(field)}
                                size="small"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#1E88E5' },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1E88E5' },
                                }}
                              />
                            }
                            label=""
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <Typography component="label" sx={fieldLabelSx}>
                            {field.label}
                            {field.required && (
                              <Box component="span" sx={{ color: '#ef4444', ml: 0.3 }}>*</Box>
                            )}
                          </Typography>
                          <TextField
                            sx={{
                              ...softInputSx,
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              '& .MuiOutlinedInput-root': {
                                ...softInputSx['& .MuiOutlinedInput-root'],
                                height: '100%',
                              }
                            }}
                            fullWidth
                            select={field.type === 'select'}
                            placeholder={getPlaceholder(field)}
                            value={form[field.name] ?? ''}
                            onChange={onChange(field)}
                            multiline={field.type === 'textarea'}
                            minRows={field.type === 'textarea' ? 3 : undefined}
                            type={field.type === 'number' ? 'number' : 'text'}
                            size="small"
                            InputLabelProps={{ shrink: false }}
                            SelectProps={field.type === 'select' ? {
                              displayEmpty: true,
                              renderValue: (value) => (value
                                ? value
                                : <Box component="span" sx={selectPlaceholderSx}>{field.placeholder || `Select ${field.label}`}</Box>),
                            } : undefined}
                          >
                            {field.type === 'select'
                              ? [
                                <MenuItem key="" value="" disabled>
                                  <em style={{ color: '#94a3b8', fontStyle: 'normal' }}>
                                    {field.placeholder || `Select ${field.label}`}
                                  </em>
                                </MenuItem>,
                                ...(field.options || []).map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                )),
                              ]
                              : null}
                          </TextField>
                          {field.helperText && (
                            <Typography sx={helperTextSx}>{field.helperText}</Typography>
                          )}
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 3.5,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              // backgroundColor: '#fafafa',
            }}
          >
            <Button className="action-btn cancel-btn action-btn cancel-btn" variant="outlined" onClick={() => navigate(backPath)} sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={onSave} sx={primaryButtonSx}>
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default ConfigCrudWorkspacePage;

ConfigCrudWorkspacePage.propTypes = {
  entityType: PropTypes.oneOf(['hcp', 'drug']),
};

ConfigCrudWorkspacePage.defaultProps = {
  entityType: null,
};
